import { HugeiconsIcon } from '@hugeicons/react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import * as styles from './basic.module.css';
import {
  Add01Icon,
  Cancel01Icon,
  PlusSignIcon,
} from '@hugeicons/core-free-icons';
import { ProductMedia } from '../File';

export type UploadItem = File | ProductMedia;

export type FileUploadView = 'compact' | 'expanded';
export type FileUploadSize = 'sm' | 'md' | 'lg';
export type FileUploadAddButtonSize = 'xs';

const ADD_BUTTON_ICON_SIZE: Record<FileUploadAddButtonSize, number> = {
  xs: 16,
};

export type DefaultFileUploadProps<T extends UploadItem = UploadItem> = {
  view: FileUploadView;
  size: FileUploadSize;
  addButtonSize?: FileUploadAddButtonSize;
  fullWidth: boolean;
  fullHeight: boolean;
  isDragging: boolean;

  handleDragEnter: (e: React.DragEvent<HTMLDivElement>) => void;
  handleDragLeave: (e: React.DragEvent<HTMLDivElement>) => void;
  handleDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  handleDrop: (e: React.DragEvent<HTMLDivElement>) => void;

  handleClick: () => void;

  files: T[];
  limit: number;

  getItemKey: (item: T) => string;
  getPreviewSrc: (item: T) => string | null | undefined;
  getLabel: (item: T) => string;

  removeItem: (item: T) => void;

  inputRef: React.RefObject<HTMLInputElement | null>;
  handleChange: (
    incoming: File[] | FileList | null,
    e?: React.ChangeEvent<HTMLInputElement>
  ) => void;

  accept?: string;
};

const PREVIEW_SIZE: Record<FileUploadSize, number> = {
  sm: 66,
  md: 88,
  lg: 101,
};

const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.2;

const clampZoom = (value: number) =>
  Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Number(value.toFixed(2))));

export function BasicFileUpload<T extends UploadItem>({
  view,
  size,
  addButtonSize,
  fullWidth,
  fullHeight,
  isDragging,
  handleDragEnter,
  handleDragLeave,
  handleDragOver,
  handleDrop,
  handleClick,
  files,
  limit,
  getItemKey,
  getPreviewSrc,
  removeItem,
  getLabel,
  inputRef,
  handleChange,
  accept,
}: DefaultFileUploadProps<T>) {
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const [zoom, setZoom] = useState(MIN_ZOOM);

  const closeLightbox = () => {
    setLightboxSrc(null);
    setZoom(MIN_ZOOM);
  };

  const openLightbox = (src: string) => {
    setLightboxSrc(src);
    setZoom(MIN_ZOOM);
  };

  useEffect(() => {
    if (!lightboxSrc) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeLightbox();
        return;
      }

      if (event.key === '+' || event.key === '=') {
        setZoom(current => clampZoom(current + ZOOM_STEP));
      }

      if (event.key === '-') {
        setZoom(current => clampZoom(current - ZOOM_STEP));
      }
    };

    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [lightboxSrc]);

  return (
    <div
      className={[
        styles.root,
        view === 'expanded' ? styles.expanded : styles.compact,
        styles[size],
        fullWidth ? styles.fullWidth : '',
        fullHeight ? styles.fullHeight : '',
        isDragging ? styles.dragging : '',
        addButtonSize ? styles[`addButton_${addButtonSize}`] : '',
      ].join(' ')}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <div className={styles.files}>
        {files.map(item => {
          const key = getItemKey(item);
          const src = getPreviewSrc(item);

          return (
            <div key={key} className={styles.card}>
              <div
                className={[styles.file, src ? styles.filePreview : ''].join(
                  ' '
                )}
              >
                <button
                  className={styles.remove}
                  onClick={() => removeItem(item)}
                  type="button"
                >
                  <HugeiconsIcon icon={Cancel01Icon} size={18} />
                </button>

                {src ? (
                  <button
                    type="button"
                    className={styles.previewOpen}
                    aria-label="Увеличить изображение"
                    onClick={() => openLightbox(src)}
                  >
                    <img
                      src={src}
                      alt="file image"
                      className={styles.image}
                      {...(fullWidth && fullHeight
                        ? {}
                        : {
                            width: PREVIEW_SIZE[size],
                            height: PREVIEW_SIZE[size],
                          })}
                    />
                  </button>
                ) : (
                  <span className={styles.filename} title={getLabel(item)}>
                    {getLabel(item)}
                  </span>
                )}
              </div>
            </div>
          );
        })}

        {files.length < limit ? (
          <button
            className={styles.upload}
            onClick={handleClick}
            type="button"
            aria-label="Добавить файл"
          >
            <HugeiconsIcon
              icon={Add01Icon}
              size={
                addButtonSize
                  ? ADD_BUTTON_ICON_SIZE[addButtonSize]
                  : size === 'sm'
                    ? 24
                    : size === 'lg'
                      ? 36
                      : 28
              }
            />
          </button>
        ) : null}
      </div>

      <input
        ref={inputRef}
        type="file"
        className={styles.input}
        onChange={e => handleChange(e.target.files, e)}
        accept={accept ?? '*/*'}
        multiple={limit > 1}
      />

      {isDragging && (
        <div className={styles.overlay} aria-hidden="true">
          <div className={styles.overlayBox}>
            <div className={styles.overlayTitle}>Отпустите файлы</div>
            <div className={styles.overlayHint}>
              {limit > 1
                ? `Можно до ${limit} файлов`
                : 'Можно только один файл'}
            </div>
          </div>
        </div>
      )}

      {lightboxSrc &&
        createPortal(
          <div className={styles.lightbox} role="dialog" aria-modal="true">
            <button
              type="button"
              className={styles.lightboxBackdrop}
              aria-label="Закрыть просмотр"
              onClick={closeLightbox}
            />

            <div className={styles.lightboxToolbar}>
              <button
                type="button"
                className={styles.lightboxControl}
                aria-label="Уменьшить"
                disabled={zoom <= MIN_ZOOM}
                onClick={() =>
                  setZoom(current => clampZoom(current - ZOOM_STEP))
                }
              >
                <span aria-hidden="true">−</span>
              </button>
              <button
                type="button"
                className={styles.lightboxZoomValue}
                aria-label="Сбросить масштаб"
                onClick={() => setZoom(MIN_ZOOM)}
              >
                {Math.round(zoom * 100)}%
              </button>
              <button
                type="button"
                className={styles.lightboxControl}
                aria-label="Увеличить"
                disabled={zoom >= MAX_ZOOM}
                onClick={() =>
                  setZoom(current => clampZoom(current + ZOOM_STEP))
                }
              >
                <HugeiconsIcon icon={PlusSignIcon} size={18} />
              </button>
              <button
                type="button"
                className={styles.lightboxControl}
                aria-label="Закрыть"
                onClick={closeLightbox}
              >
                <HugeiconsIcon icon={Cancel01Icon} size={18} />
              </button>
            </div>

            <div
              className={styles.lightboxStage}
              onClick={event => {
                if (event.target === event.currentTarget) {
                  closeLightbox();
                }
              }}
              onWheel={event => {
                event.preventDefault();
                const delta = event.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
                setZoom(current => clampZoom(current + delta));
              }}
            >
              <img
                src={lightboxSrc}
                alt="Увеличенное изображение"
                className={styles.lightboxImage}
                style={{ transform: `scale(${zoom})` }}
                draggable={false}
                onClick={event => {
                  event.stopPropagation();
                  setZoom(current =>
                    current >= MAX_ZOOM
                      ? MIN_ZOOM
                      : clampZoom(current + ZOOM_STEP)
                  );
                }}
              />
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
